// Admin: Tagesmaterial für AZAV-Maßnahmen hochladen und verwalten.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, Upload, Download, Loader2, FolderOpen } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { fileIcon, formatSize, downloadTagesmaterialFile } from '../components/TagesmaterialCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AZAV_COURSES = [
  { id: 'course-behandlungspflege-lg', label: 'Behandlungspflege LG1 + LG2', nr: '364/1013/2026', ue: '208 UE' },
  { id: 'course-ambulanter-pflegeassistent', label: 'Ambulanter Pflegeassistent', nr: '364/1045/2026', ue: '300 UE' },
  { id: 'course-pflegehilfskraft', label: 'Pflegehilfskraft mit Betreuung', nr: '364/1232/2024', ue: '429 UE inkl. Praktikum' },
];

export default function AdminTagesmaterialPage({ Layout }) {
  const { getAuthHeader } = useAuth();
  const [courseId, setCourseId] = useState(AZAV_COURSES[0].id);
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [tagNummer, setTagNummer] = useState('');
  const [tagTitel, setTagTitel] = useState('');
  const [datum, setDatum] = useState('');
  const [files, setFiles] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tagesmaterial/kurs/${courseId}`, getAuthHeader());
      setDays(res.data.days || []);
    } catch {
      toast.error('Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }, [courseId, getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    const nr = parseInt(tagNummer, 10);
    if (!nr || nr < 1) return toast.error('Bitte Tag-Nummer angeben');
    if (files.length === 0) return toast.error('Bitte mindestens eine Datei wählen');
    setUploading(true);
    let ok = 0;
    for (const f of files) {
      const fd = new FormData();
      fd.append('course_id', courseId);
      fd.append('tag_nummer', String(nr));
      fd.append('tag_titel', tagTitel);
      fd.append('datum', datum);
      fd.append('file', f);
      try {
        await axios.post(`${API}/tagesmaterial/upload`, fd, getAuthHeader());
        ok += 1;
      } catch (e) {
        toast.error(`${f.name}: ${e.response?.data?.detail || 'Upload fehlgeschlagen'}`);
      }
    }
    setUploading(false);
    if (ok > 0) {
      toast.success(`${ok} Datei(en) für Tag ${nr} hochgeladen`);
      setFiles([]);
      setTagTitel('');
      setDatum('');
      document.getElementById('tagesmaterial-file-input').value = '';
      load();
    }
  };

  const remove = async (fileId) => {
    try {
      await axios.delete(`${API}/tagesmaterial/${fileId}`, getAuthHeader());
      toast.success('Datei entfernt');
      load();
    } catch {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const active = AZAV_COURSES.find((c) => c.id === courseId);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="admin-tagesmaterial-page">
        <div>
          <p className="font-mono text-[10px] uppercase font-semibold tracking-[0.25em] text-terra mb-1">AZAV-Maßnahmen</p>
          <h1 className="font-display text-3xl text-espresso">Tagesmaterial</h1>
          <p className="text-[13px] text-sand-600 mt-1">Material pro Unterrichtstag hochladen — Teilnehmer laden es im Kursbereich herunter.</p>
        </div>

        <div className="flex flex-wrap gap-2">
          {AZAV_COURSES.map((c) => (
            <button
              key={c.id}
              onClick={() => setCourseId(c.id)}
              className={`px-4 py-2.5 rounded-xl text-[12px] font-semibold border transition-colors ${courseId === c.id ? 'bg-espresso text-cream-50 border-espresso' : 'bg-cream-50 text-sand-600 border-cream-300 hover:bg-cream-100'}`}
              data-testid={`tab-${c.id}`}
            >
              {c.label}
              <span className={`block text-[10px] font-normal ${courseId === c.id ? 'text-cream-200' : 'text-sand-500'}`}>Maßnahme {c.nr} · {c.ue}</span>
            </button>
          ))}
        </div>

        <div className="bg-cream-50 border border-cream-300 rounded-2xl p-6 space-y-4" data-testid="tagesmaterial-upload-form">
          <h2 className="font-display text-lg text-espresso flex items-center gap-2"><Upload className="w-4 h-4 text-terra" /> Material hochladen — {active?.label}</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-sand-600 uppercase tracking-wider">Tag-Nummer *</label>
              <input type="number" min="1" value={tagNummer} onChange={(e) => setTagNummer(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-cream-300 bg-white text-sm" placeholder="z. B. 12"
                data-testid="input-tag-nummer" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-sand-600 uppercase tracking-wider">Titel (optional)</label>
              <input value={tagTitel} onChange={(e) => setTagTitel(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-cream-300 bg-white text-sm" placeholder="z. B. Prophylaxen"
                data-testid="input-tag-titel" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-sand-600 uppercase tracking-wider">Datum (optional)</label>
              <input value={datum} onChange={(e) => setDatum(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-cream-300 bg-white text-sm" placeholder="z. B. 24.09.2026"
                data-testid="input-tag-datum" />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-sand-600 uppercase tracking-wider">Dateien (PDF, PowerPoint, Word, Bilder — mehrere möglich)</label>
            <input id="tagesmaterial-file-input" type="file" multiple
              accept=".pdf,.ppt,.pptx,.doc,.docx,.odt,.png,.jpg,.jpeg,.gif,.webp"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              className="mt-1 w-full text-sm text-sand-600 file:mr-3 file:px-4 file:py-2 file:rounded-full file:border-0 file:bg-espresso file:text-cream-50 file:text-[11px] file:font-bold file:uppercase file:tracking-wider file:cursor-pointer"
              data-testid="input-tag-files" />
            {files.length > 0 && <p className="text-[11px] text-sand-500 mt-1">{files.length} Datei(en) ausgewählt</p>}
          </div>
          <button onClick={upload} disabled={uploading}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-terra text-white text-[12px] font-bold uppercase tracking-wider hover:opacity-90 disabled:opacity-50"
            data-testid="btn-upload-tagesmaterial">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? 'Lädt hoch…' : 'Hochladen'}
          </button>
        </div>

        <div className="space-y-3">
          {loading && <p className="text-sm text-sand-500">Lade…</p>}
          {!loading && days.length === 0 && (
            <div className="text-center py-10 bg-cream-50 border border-dashed border-cream-300 rounded-2xl">
              <FolderOpen className="w-8 h-8 text-sand-400 mx-auto mb-2" />
              <p className="text-sm text-sand-500">Noch kein Material für diese Maßnahme hochgeladen.</p>
            </div>
          )}
          {!loading && days.map((day) => (
            <div key={day.tag_nummer} className="bg-cream-50 border border-cream-300 rounded-2xl p-5" data-testid={`admin-tag-${day.tag_nummer}`}>
              <div className="flex items-baseline gap-2 mb-3">
                <span className="font-display text-xl text-terra-dark">Tag {day.tag_nummer}</span>
                {day.tag_titel && <span className="text-[13px] font-semibold text-espresso">— {day.tag_titel}</span>}
                {day.datum && <span className="text-[11px] text-sand-500 ml-auto">{day.datum}</span>}
              </div>
              <div className="space-y-1.5">
                {day.files.map((f) => {
                  const Icon = fileIcon(f.original_filename);
                  return (
                    <div key={f.id} className="flex items-center gap-2.5 px-3 py-2 rounded-md bg-cream-100 border border-cream-300">
                      <Icon className="w-4 h-4 text-terra shrink-0" strokeWidth={1.7} />
                      <span className="flex-1 text-[12.5px] text-espresso truncate">{f.original_filename}</span>
                      <span className="text-[10.5px] text-sand-500">{formatSize(f.size)}</span>
                      <button onClick={() => downloadTagesmaterialFile(f, getAuthHeader)} className="p-1.5 rounded hover:bg-cream-200" title="Herunterladen" data-testid={`admin-download-${f.id}`}>
                        <Download className="w-3.5 h-3.5 text-sand-600" />
                      </button>
                      <button onClick={() => remove(f.id)} className="p-1.5 rounded hover:bg-red-50" title="Entfernen" data-testid={`admin-delete-${f.id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
